import React from "react"
import { formatDateTime, formatScore } from "../../lib/format"
import {
  ERRO_NO_PRIMEIRO,
  OBJECAO_FONTE,
  OBJECAO_FONTE_LABEL,
  OBJECAO_NUMERO,
  corteLabel,
  faixaLabel,
  formatCorte,
} from "../../lib/narrativa"
import { ListaSelector } from "./ListaSelector"
import type { Corte, Cortes, Faixa, NotaBanca } from "../../types"

interface Props {
  school: string
  /** Corte da lista do candidato (nulo sem lista ou sem corte publicado). */
  corte: Corte | null
  /** Todas as listas com corte, para o seletor; nulo para escola sem corte. */
  cortes: Cortes | null
  lista: string | null
  onLista: (lista: string) => void
  /** Nota pela fórmula do edital; nula para escola sem fórmula publicada. */
  notaBanca: NotaBanca | null
  /** Faixa de referência quando a escola não tem corte único publicado. */
  faixa: Faixa | null
  /** Este é o nivelamento (1º simulado). */
  first: boolean
  /** Simulado no formato da banca (conta pro critério de pronto). */
  oficial: boolean
  /** Treino focado (fortalecimento/específico), não a prova inteira. */
  treino: boolean
  /** Passou do corte da sua lista? null = sem como decidir (sem lista, sem corte, sem nota de banca). */
  passou: boolean | null
  score: number
  correctCount: number
  totalQuestions: number
  startedAt: string
  expired: boolean
}

// Medidor de nota em tema escuro (pro hero navy). Anel dourado + número branco.
function ScorePanel({ score, correctCount, totalQuestions, startedAt, expired, notaBanca }: Pick<Props, "score" | "correctCount" | "totalQuestions" | "startedAt" | "expired" | "notaBanca">) {
  const r = 46
  const c = 2 * Math.PI * r
  const shown = notaBanca?.completa ? notaBanca.nota : score
  const filled = Math.min(Math.max(shown / 10, 0), 1)
  return (
    <div className="flex shrink-0 flex-col items-center gap-2 md:w-48 md:border-l md:border-white/10 md:pl-6">
      <div className="relative h-28 w-28" role="img" aria-label={`Nota ${notaBanca?.completa ? formatCorte(shown) : formatScore(shown)} de 10`}>
        <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
          <circle cx="60" cy="60" r={r} fill="none" stroke="currentColor" strokeWidth="9" className="text-white/10" />
          <circle
            cx="60"
            cy="60"
            r={r}
            fill="none"
            stroke="currentColor"
            strokeWidth="9"
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={c * (1 - filled)}
            className="text-gold-400"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className="text-4xl font-extrabold text-white tabular-nums">{notaBanca?.completa ? formatCorte(shown) : formatScore(shown)}</p>
          <p className="text-[11px] font-medium text-navy-300">{notaBanca?.completa ? "pela banca" : "de 10"}</p>
        </div>
      </div>
      <p className="text-sm font-semibold text-white">
        {correctCount}/{totalQuestions} acertos
      </p>
      <p className="text-center text-[11px] leading-snug text-navy-300">
        Simulado de {formatDateTime(startedAt)}
        {expired && " · finalizado por tempo"}
      </p>
    </div>
  )
}

// Divisas da marca, decorativas (mesmo motivo do ProvaCard).
function Chevrons() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="pointer-events-none absolute -right-8 -top-8 h-44 w-44 text-white/[0.04]"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="square"
      aria-hidden="true"
    >
      <path d="M4 8.5 12 3.5 20 8.5" />
      <path d="M4 14 12 9 20 14" />
      <path d="M4 19.5 12 14.5 20 19.5" />
    </svg>
  )
}

/**
 * A manchete é o veredito, não a nota. "0,0" em letra gigante é feedback sobre
 * a pessoa, o tipo que piora o desempenho em um terço dos casos (Kluger e
 * DeNisi, 1996); "ainda não classificaria, e é isto que falta" é feedback
 * sobre a tarefa. A nota continua na tela, como apoio.
 *
 * O veredito só existe na régua certa: nota pela fórmula do edital contra o
 * corte DA LISTA do candidato. Sem lista escolhida, o hero pergunta a lista e
 * não decide. Escola sem corte publicado: nota, faixa e nenhum veredito.
 */
export function ResultHero({ school, corte, cortes, lista, onLista, notaBanca, faixa, first, oficial, treino, passou, score, correctCount, totalQuestions, startedAt, expired }: Props) {
  const eyebrow = first ? "Etapa 1 de 5 · Nivelamento concluído" : oficial ? "Etapa 4 · Simulado oficial" : treino ? "Treino concluído" : "Simulado concluído"
  const notaBancaLabel = notaBanca ? formatCorte(notaBanca.nota) : null
  const feito = first ? "nivelamento" : oficial ? "simulado" : treino ? "treino" : "simulado"

  let headline: string
  if (passou === true) {
    headline = first
      ? "Você passaria do corte da sua lista — no nivelamento. Ainda não é a prova."
      : oficial
        ? "Você passou do corte da sua lista neste simulado 🔥"
        : `Você passou do corte da sua lista neste ${feito}.`
  } else if (passou === false) {
    headline = "Hoje você ainda não classificaria."
  } else if (cortes && notaBanca?.completa) {
    headline = `Pela fórmula da banca, sua nota é ${notaBancaLabel}.`
  } else if (notaBanca && !notaBanca.completa) {
    headline = `${notaBanca.partes.map((p) => p.nome).join(" e ")}: ${notaBancaLabel} pela fórmula da banca neste ${feito}.`
  } else {
    headline = `${feito.charAt(0).toUpperCase()}${feito.slice(1)} concluído: ${correctCount} de ${totalQuestions}.`
  }

  const maior = cortes ? cortes.listas.find((l) => l.key === cortes.maior) : null
  const apoio = [
    notaBanca?.completa
      ? `Você acertou ${correctCount} de ${totalQuestions}: nota simples ${formatScore(score)}, ${notaBancaLabel} pela fórmula do edital (média das partes).`
      : `Você acertou ${correctCount} de ${totalQuestions} (nota ${formatScore(score)}).`,
    corte ? `O corte da última ${school} ficou em ${corteLabel(corte)}.` : null,
    !corte && cortes && maior && notaBanca?.completa
      ? `O maior corte da área ${cortes.area} em ${cortes.ano} foi ${formatCorte(maior.nota)}; escolha sua lista para ver o seu.`
      : null,
    // Sem corte único, a faixa é contexto — dita como faixa, com o porquê de
    // não ser um número só. Nunca decide o veredito acima.
    !cortes && faixa ? `A ${school} não publica um corte único: nas edições recentes ele ficou ${faixaLabel(faixa)} ${faixa.detalhe}.` : null,
    passou === true && first ? "O formato da prova tem mais questões, tempo contado e o mesmo corte." : null,
    first && cortes ? "Com 10 questões, é um primeiro retrato: a etapa 2 confirma o padrão matéria por matéria." : null,
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <div className="relative overflow-hidden rounded-2xl bg-navy-900 p-6 shadow-lg shadow-navy-900/20 sm:p-8">
      <Chevrons />
      <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gold-400">{eyebrow}</p>
          <h2 className="mt-2 font-display text-2xl font-bold uppercase leading-tight tracking-tight text-white sm:text-3xl">
            {headline}
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-navy-200">{apoio}</p>
          {cortes && notaBanca?.completa && <ListaSelector cortes={cortes} lista={lista} onChange={onLista} dark />}
          {/* Normaliza o erro no nivelamento: é o momento em que nasce a crença
              "preciso estudar antes de fazer questões" — e a resposta é o número. */}
          {first && passou !== true && (
            <p className="mt-3 max-w-xl text-xs leading-relaxed text-navy-300">
              Errar no nivelamento é o esperado — {ERRO_NO_PRIMEIRO}. Ele existe pra mostrar por onde começar, e não é
              consolo: {OBJECAO_NUMERO} (
              <a href={OBJECAO_FONTE} className="underline decoration-gold-500/60 underline-offset-2 hover:text-gold-300" target="_blank" rel="noopener">
                {OBJECAO_FONTE_LABEL}
              </a>
              ).
            </p>
          )}
        </div>

        <ScorePanel
          score={score}
          notaBanca={notaBanca}
          correctCount={correctCount}
          totalQuestions={totalQuestions}
          startedAt={startedAt}
          expired={expired}
        />
      </div>
    </div>
  )
}
